import {
  useEffect,
  useId,
  useMemo,
  useState,
  type KeyboardEvent,
  type PointerEvent,
  type RefObject,
} from 'react'
import { Icon } from './Icon'

interface TagAutocompleteProps {
  inputRef: RefObject<HTMLInputElement | null>
  onAddTag: (tag: string) => void
  onClose: () => void
  onRemoveTag: (tag: string) => void
  onValueChange: (value: string) => void
  selectedTags: readonly string[]
  showSuggestionsWhenEmpty?: boolean
  tags: readonly string[]
  value: string
}

interface AutocompleteOption {
  kind: 'existing' | 'new'
  tag: string
}

export function normalizeTagQuery(value: string): string {
  return value.trim().toLowerCase().replace(/^#/, '')
}

export function TagAutocomplete({
  inputRef,
  onAddTag,
  onClose,
  onRemoveTag,
  onValueChange,
  selectedTags,
  showSuggestionsWhenEmpty = true,
  tags,
  value,
}: TagAutocompleteProps) {
  const listboxId = useId()
  const normalizedQuery = normalizeTagQuery(value)
  const matchingTags = useMemo(
    () => tags
      .filter((tag) => !selectedTags.includes(tag))
      .filter((tag) => !normalizedQuery || tag.includes(normalizedQuery))
      .slice(0, 6),
    [normalizedQuery, selectedTags, tags],
  )
  const shouldOfferNewTag = Boolean(normalizedQuery) && !tags.includes(normalizedQuery)
  const options = useMemo<AutocompleteOption[]>(
    () => [
      ...matchingTags.map((tag) => ({ kind: 'existing' as const, tag })),
      ...(shouldOfferNewTag ? [{ kind: 'new' as const, tag: normalizedQuery }] : []),
    ],
    [matchingTags, normalizedQuery, shouldOfferNewTag],
  )
  const suggestionsVisible = showSuggestionsWhenEmpty || Boolean(normalizedQuery)
  const visibleOptions = suggestionsVisible ? options : []
  const [activeOptionIndex, setActiveOptionIndex] = useState<number | null>(null)

  useEffect(() => {
    setActiveOptionIndex(null)
  }, [normalizedQuery, selectedTags.length])

  const selectOption = (option: AutocompleteOption) => {
    onAddTag(option.tag)
    onValueChange('')
    inputRef.current?.focus({ preventScroll: true })
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      event.stopPropagation()
      onClose()
      return
    }
    if (event.key === 'Backspace' && !value && selectedTags.length > 0) {
      event.preventDefault()
      onRemoveTag(selectedTags[selectedTags.length - 1]!)
      return
    }
    if (event.key === 'Enter' && !normalizedQuery && activeOptionIndex === null) {
      event.preventDefault()
      onClose()
      return
    }
    if (!visibleOptions.length) return
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      const direction = event.key === 'ArrowDown' ? 1 : -1
      setActiveOptionIndex((current) => {
        if (current === null) return direction > 0 ? 0 : visibleOptions.length - 1
        return (current + direction + visibleOptions.length) % visibleOptions.length
      })
      return
    }
    if (event.key === 'Enter') {
      event.preventDefault()
      if (activeOptionIndex !== null && visibleOptions[activeOptionIndex]) {
        selectOption(visibleOptions[activeOptionIndex])
        return
      }
      if (!normalizedQuery) return
      const exactTag = tags.find((tag) => tag === normalizedQuery)
      if (exactTag && !selectedTags.includes(exactTag)) selectOption({ kind: 'existing', tag: exactTag })
      else if (!exactTag) selectOption({ kind: 'new', tag: normalizedQuery })
    }
  }

  const activeOptionId = activeOptionIndex !== null && visibleOptions[activeOptionIndex]
    ? `${listboxId}-${activeOptionIndex}`
    : undefined
  const preserveInputFocusForMouse = (event: PointerEvent<HTMLButtonElement>) => {
    // Cancelling touch pointerdown can suppress the compatibility click in Android WebView.
    // Mouse selection still keeps the editor from stealing focus on desktop.
    if (event.pointerType === 'mouse') event.preventDefault()
  }

  return (
    // Scrolled above a phone keyboard whole, not only its field (utils/visualViewport.ts, revealFocusedLine).
    <div className="card card-border tag-editor" aria-label="Edit tags" data-reveal-whole>
      <div className="tag-editor__input-row">
        {selectedTags.map((tag) => (
          <button
            type="button"
            key={tag}
            className="btn btn-ghost tag-editor__selected-chip"
            onPointerDown={preserveInputFocusForMouse}
            onClick={() => onRemoveTag(tag)}
            aria-label={`Remove tag ${tag}`}
          >
            <span>{tag}</span>
            <Icon name="close" aria-hidden="true" />
          </button>
        ))}
        <input
          ref={inputRef}
          className="input input-ghost tag-input"
          value={value}
          onChange={(event) => onValueChange(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Search or create a tag…"
          aria-label="Search or create a tag"
          aria-autocomplete="list"
          aria-controls={suggestionsVisible ? listboxId : undefined}
          aria-expanded={suggestionsVisible}
          aria-activedescendant={activeOptionId}
          role="combobox"
        />
      </div>

      {suggestionsVisible && <div className="tag-editor__suggestions">
        {visibleOptions.length > 0 ? (
          <ul
            id={listboxId}
            className="menu menu-sm menu-vertical tag-editor__suggestion-list"
            role="listbox"
            aria-label="Tag suggestions"
          >
            {visibleOptions.map((option, index) => (
              <li key={`${option.kind}-${option.tag}`} role="presentation">
                <button
                  id={`${listboxId}-${index}`}
                  type="button"
                  role="option"
                  aria-selected={index === activeOptionIndex}
                  className={`btn btn-ghost tag-editor__suggestion${option.kind === 'new' ? ' tag-editor__suggestion--create' : ''}${index === activeOptionIndex ? ' tag-editor__suggestion--active' : ''}`}
                  onPointerDown={preserveInputFocusForMouse}
                  onPointerMove={(event) => {
                    if (event.pointerType === 'mouse') setActiveOptionIndex(index)
                  }}
                  onClick={() => selectOption(option)}
                >
                  <span className="tag-editor__suggestion-plus" aria-hidden="true">+</span>
                  <span>{option.kind === 'new' ? `Create "${option.tag}"` : option.tag}</span>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <div id={listboxId} className="tag-editor__empty" role="status">
            No tags yet — start typing to create one
          </div>
        )}
      </div>}

      <div className="tag-editor__actions">
        <button type="button" className="btn btn-primary tag-editor__done" onClick={onClose}>
          Done
        </button>
      </div>
    </div>
  )
}
